import { Modal, ModalProps } from '@/components/basic/Modal';
import CreatePermissionForm from '@/components/permissions/CreatePermission/CreatePermissionForm';
import { CreatePermissionProvider, useCreatePermissionProvider } from '@/components/permissions/CreatePermission/CreatePermissionProvider';
import { AddAssignmentModalFooter } from '@/components/resources/modals/AddAssignmentModal/AddAssignmentModalFooter';
import { accessAssignmentGroups } from '@/components/resources/tabs/access-restrictions/defines';
import { AssignmentGroupType, IResource } from '@gettactic/api';
import { useQueryClient } from '@tanstack/react-query';
import React from 'react';

export interface AddAssignmentModal extends Omit<ModalProps, 'children'> {
  onCreated: () => void;
  resources: IResource[];
  group: AssignmentGroupType;
}

const AddAssignmentModalContent: React.FC<AddAssignmentModal> = (props) => {
  const { resources, onCreated, onClose, group, ...rest } = props;
  const queryClient = useQueryClient();
  const { resetToDefaults } = useCreatePermissionProvider();

  const invalidateAll = async () => {
    await Promise.all([
      queryClient.invalidateQueries([
        'organizations.resourceScheduledPermissionsAll'
      ]),
      queryClient.invalidateQueries([
        'organizations.getResourceAdditionalAccess'
      ])
    ]);
  };

  const handleOnClose = async () => {
    await invalidateAll();
    resetToDefaults();
    onClose();
  };

  const handleOnCreated = async () => {
    await invalidateAll();
    onCreated?.();
  };

  const selectedGroup = accessAssignmentGroups[group];

  return (
    <Modal
      onClose={handleOnClose}
      title={`Add Assignments / ${selectedGroup?.label}`}
      size="max-w-[45rem]"
      {...rest}
    >
      <CreatePermissionForm isOpen={rest.opened} />
      <AddAssignmentModalFooter onClose={onClose} />
    </Modal>
  );
};

export const AddAssignmentModal: React.FC<AddAssignmentModal> = (props) => {
  const { resources, onCreated, group } = props;

  return (
    <CreatePermissionProvider
      key={group}
      group={group}
      resources={resources}
      onCreated={onCreated}
      onSave={() => {}}
    >
      <AddAssignmentModalContent {...props} />
    </CreatePermissionProvider>
  );
};
